import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { dashboardAPI, leaveAPI, userAPI } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { formatDate, capitalizeFirst, getStatusColor } from "../../lib/utils";
import {
  Users,
  UserCheck,
  CalendarDays,
  ArrowRight,
  CheckCircle,
  XCircle,
} from "lucide-react";

const ManagerDashboard = () => {
  const [stats, setStats] = useState(null);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, leavesRes, usersRes] = await Promise.all([
        dashboardAPI.getStats(),
        leaveAPI.getAll({ status: "pending" }),
        userAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setPendingLeaves(leavesRes.data.slice(0, 5));
      setTeamMembers(usersRes.data.slice(0, 5));
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveLeave = async (leaveId) => {
    try {
      await leaveAPI.approve(leaveId);
      toast.success("Leave approved successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve leave");
    }
  };

  const handleRejectLeave = async (leaveId) => {
    try {
      await leaveAPI.reject(leaveId, "Rejected by manager");
      toast.success("Leave rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject leave");
    }
  };

  const statCards = [
    {
      title: "Team Size",
      value: stats?.team_size || 0,
      icon: Users,
      color: "bg-indigo-100 text-indigo-600",
    },
    {
      title: "Present Today",
      value: stats?.team_present_today || 0,
      icon: UserCheck,
      color: "bg-emerald-100 text-emerald-600",
    },
    {
      title: "Pending Approvals",
      value: stats?.pending_leave_approvals || 0,
      icon: CalendarDays,
      color: "bg-amber-100 text-amber-600",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="manager-dashboard">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Manager Dashboard
        </h1>
        <p className="text-slate-500 mt-1">
          Manage your team and approve requests
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card card-hover" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                </div>
                <div className={`stat-card-icon ${stat.color}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Leave Approvals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Pending Leave Approvals</CardTitle>
            <Link to="/leaves">
              <Button variant="ghost" size="sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingLeaves.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No pending approvals</p>
              ) : (
                pendingLeaves.map((leave) => (
                  <div
                    key={leave.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-50"
                  >
                    <div>
                      <p className="font-medium text-slate-900">
                        {capitalizeFirst(leave.leave_type)} Leave
                      </p>
                      <p className="text-sm text-slate-500">
                        {formatDate(leave.start_date)} - {formatDate(leave.end_date)} ({leave.days} days)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        onClick={() => handleApproveLeave(leave.id)}
                        data-testid={`approve-leave-${leave.id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRejectLeave(leave.id)}
                        data-testid={`reject-leave-${leave.id}`}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Team Members */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Team Members</CardTitle>
            <Link to="/attendance/reports">
              <Button variant="ghost" size="sm">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {teamMembers.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No team members</p>
              ) : (
                teamMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="avatar">
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {member.first_name} {member.last_name}
                        </p>
                        <p className="text-sm text-slate-500">{member.email}</p>
                      </div>
                    </div>
                    <Badge variant={member.is_active ? "default" : "secondary"}>
                      {member.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ManagerDashboard;
